import React from 'react';
import { SparkButton } from 'sharedComponents/UI';
import { InputIcon, OutputIcon } from 'components/SVG';
import { Box } from '@mui/material';
import { NODE_MIN_WIDTH } from '../../hooks';
import { SparkStepNodeProps } from '../SparkStepNode';
import { useSparkBuilderStore } from 'stores';
import { useFormByID } from 'hooks/Forms';
import { parseJSONCleaned } from 'utilities/index';
import { statusConfigByStatusTag } from '../statusConfigByStatusTag';
import { useBoardNodesByBoardID } from 'hooks/Board';
import { SPARK_DRAWER_TYPES } from 'constants/common';

function CustomSparkInputOutputValuesNode({ data }: SparkStepNodeProps) {
  const joinedSpark = useSparkBuilderStore(
    React.useCallback((state) => state.joinedSpark, [])
  );
  const outputBoardID = joinedSpark?.attributes.outputBoardID;
  const { boardNodes } = useBoardNodesByBoardID(outputBoardID);
  const totalNodes = boardNodes?.length;

  const { type } = data;
  const inputFormID = useSparkBuilderStore(
    (state) => state.joinedSpark?.attributes.inputFormID
  );
  const { form } = useFormByID(inputFormID);

  const inputParameters = parseJSONCleaned<any>(form?.attributes.config)
    ?.pages?.map((item: any) => item.elements)
    .flat();

  const inputParametersCount = inputParameters?.length || 0;

  const handleStepClick = useSparkBuilderStore(
    (store) => store.builderSideBarActions.setSelectedStep
  );

  const warningConfig = statusConfigByStatusTag['warning'];

  const styles = React.useMemo(() => {
    if (type === SPARK_DRAWER_TYPES.input) {
      return {
        borderColor: !inputParametersCount ? warningConfig.borderColor : '',
        Icon: !inputParametersCount ? warningConfig.Icon : '',
      };
    } else {
      return {
        borderColor: !totalNodes ? warningConfig.borderColor : '',
        Icon: !totalNodes ? warningConfig.Icon : '',
      };
    }
  }, [
    inputParametersCount,
    totalNodes,
    type,
    warningConfig.Icon,
    warningConfig.borderColor,
  ]);
  if (!type) return <></>;

  const inputText =
    inputParametersCount === 1 ? '1 INPUT' : `${inputParametersCount} INPUTS`;
  const outputText = 'OUTPUT';

  const displayText =
    type === SPARK_DRAWER_TYPES.input ? inputText : outputText;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        minWidth: `${NODE_MIN_WIDTH + 30}px`,
        marginBottom: type === SPARK_DRAWER_TYPES.input ? '-4px' : '0px',
        marginTop: type === SPARK_DRAWER_TYPES.input ? '0px' : '-4px',
      }}
    >
      <SparkButton
        onClick={() => handleStepClick({ type })}
        sx={{
          bgcolor: (theme) => theme.palette.primary.light,
          color: (theme) => theme.palette.common.black,
          border: (theme) =>
            `1px solid ${styles.borderColor || theme.palette.primary.main}`,
          minHeight: '3rem',
          minWidth: '300px',
          borderRadius: '10px',
          padding: 2,
          position: 'relative',
          '&:hover': {
            background: '#eceffe',
          },
        }}
        startIcon={
          type === SPARK_DRAWER_TYPES.input ? <InputIcon /> : <OutputIcon />
        }
      >
        {styles.Icon && (
          <Box sx={{ position: 'absolute', top: -15, right: -15 }}>
            <styles.Icon />
          </Box>
        )}
        {displayText}
      </SparkButton>
    </Box>
  );
}

export default CustomSparkInputOutputValuesNode;
